import { datadogLogs } from "@datadog/browser-logs";
import {
  getSessionStorageWithExpiry,
  setSessionStorageWithExpiry,
} from "@redotech/redo-web/utils/session-storage-wrapper";
import { getObjectPath, setObjectPath } from "@redotech/util/object";
import { log } from "./log";

interface ReportTimingOptions {
  value?: number;
  overwrite?: boolean;
  sendDatadogMetric?: boolean;
  completeTiming?: boolean;
}

export function reportTiming(
  path: string,
  options: ReportTimingOptions = {},
): void {
  try {
    const {
      value = performance.now(),
      overwrite = false,
      sendDatadogMetric = false,
      completeTiming = false,
    } = options;

    if (!window.redoStorefront) {
      window.redoStorefront = { type: "shopify" as const, timing: {} };
    }

    if (!window.redoStorefront.timing) {
      window.redoStorefront.timing = {};
    }
    if (
      overwrite ||
      getObjectPath(window.redoStorefront.timing, path) == null
    ) {
      setObjectPath(window.redoStorefront.timing, path, value);

      if (sendDatadogMetric) {
        const firstLoadComplete =
          getSessionStorageWithExpiry("Redo-First-Load-Complete") === true;
        datadogLogs.logger.debug(path, {
          value,
          valueSinceRedoInitialized:
            value -
            (window.redoStorefront.timing.coverage?.redoInitialized || 0),
          load: firstLoadComplete ? "not-first" : "first",
          timing: window.redoStorefront.timing,
        });
      }
    }

    if (completeTiming) {
      setSessionStorageWithExpiry("Redo-First-Load-Complete", true, 1);
    }
  } catch (error) {
    // Silently fail to avoid breaking the application
    log("Failed to report timing:", error);
  }
}

// Initialize timing tracking with empty objects
export function initializeTiming(): void {
  try {
    if (!window.redoStorefront) {
      window.redoStorefront = { type: "shopify" as const, timing: {} };
    }

    if (!window.redoStorefront.timing) {
      window.redoStorefront.timing = {};
    }

    if (!window.redoStorefront.timing.tryFirst) {
      window.redoStorefront.timing.tryFirst = {
        pageLoad: 0,
        initializeFunctionCalled: undefined,
        autocheckCacheRetrieved: undefined,
        contextProviderAdded: undefined,
        sellingPlansRetrieved: undefined,
        eligibilityChecked: undefined,
        buttonIsEligibleToShow: undefined,
        buttonRendered: undefined,
      };
    }

    const freshTiming = {
      redoInitialized: performance.now(),
      beforeInitialize: undefined,
      beforeAutocheckCacheRetrieved: undefined,
      beforeFeesCacheRetrieved: undefined,
      beforeCartCalls: undefined,
      beforeGettingVariants: undefined,
      afterGettingVariants: undefined,
      initializeFunctionCalled: undefined,
      autocheckCacheRetrieved: undefined,
      afterFeesCacheRetrieved: undefined,
      afterCartCalls: undefined,
      eligibilityChecked: undefined,
      buttonRendered: undefined,
    };

    if (!window.redoStorefront.timing.coverage) {
      window.redoStorefront.timing.coverage = freshTiming;
    }
  } catch (error) {
    console.warn("Failed to initialize timing:", error);
  }
}
